import { CloseButton } from '@headlessui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Checkbox, Chip, Input } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import toast from '@/app/toast.ts';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/primitives/dialog.tsx';
import { TextLink } from '@/primitives/text.tsx';
import { useFetchAccountQuery } from '@/slices/account-api-slice.ts';
import { usePostAccountSiteStaffMemberMutation } from '@/slices/account-site-staff-member-api-slice.ts';
import { useFetchStaffMembersQuery } from '@/slices/staff-api-slice.ts';
import { useFetchStaffMemberRolesQuery } from '@/slices/staff-role-api-slices.ts';
import { useSecurityContext } from '../../hooks/security-provider.tsx';
import { NoData } from '../no-data.tsx';
import { usePageLoading } from '../page-loading.tsx';

const OrganizationStaffMemberDialog = ({ open, setOpen }) => {
  const { currentAccount } = useSecurityContext();
  const { data: accountData } = useFetchAccountQuery(currentAccount?.key ?? skipToken);
  const { data } = useFetchStaffMembersQuery(
    currentAccount?.organizationKey
      ? new URLSearchParams({ organizationKey: currentAccount?.organizationKey }).toString()
      : skipToken,
  );
  const { data: staffRoles } = useFetchStaffMemberRolesQuery();
  const [postAccountSiteStaffMember, { isLoading: isPosting }] =
    usePostAccountSiteStaffMemberMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStaffKeys, setSelectedStaffKeys] = useState<Set<string>>(new Set());

  const staffMembers = useMemo(() => {
    return (
      data?.filter(x => x.accountSiteStaffMembers.every(y => y.accountId !== currentAccount?.id)) ??
      []
    );
  }, [data, currentAccount?.id]);

  const filteredStaffMembers = useMemo(() => {
    if (!searchQuery.trim()) {
      return staffMembers;
    }

    const query = searchQuery.toLowerCase();
    return staffMembers.filter(staff => {
      const fullName = `${staff.firstName} ${staff.lastName}`.toLowerCase();
      const email = staff.emailAddress?.toLowerCase() || '';
      const title = staff.title?.toLowerCase() || '';
      const positions = staff.accountSiteStaffMembers
        .map(x => x.position?.toLowerCase() || '')
        .join(' ');
      const specialties = staff.accountSiteStaffMembers
        .flatMap(x => x.specialties)
        .join(' ')
        .toLowerCase();

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        title.includes(query) ||
        positions.includes(query) ||
        specialties.includes(query)
      );
    });
  }, [staffMembers, searchQuery]);

  const isAllSelected =
    filteredStaffMembers.length > 0 &&
    filteredStaffMembers.every(staff => selectedStaffKeys.has(staff.key));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedStaffKeys(new Set());
    } else {
      setSelectedStaffKeys(new Set(filteredStaffMembers.map(staff => staff.key)));
    }
  };

  const handleSelectStaff = (staffKey: string) => {
    const newSelected = new Set(selectedStaffKeys);
    if (newSelected.has(staffKey)) {
      newSelected.delete(staffKey);
    } else {
      newSelected.add(staffKey);
    }
    setSelectedStaffKeys(newSelected);
  };

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    isPosting ? showPageLoading('add-staff-members') : hidePageLoading('add-staff-members');
  }, [isPosting]);

  const handleAddSelectedUsers = async () => {
    if (selectedStaffKeys.size === 0 || !currentAccount) {
      return;
    }

    const defaultRole = staffRoles?.find(r => r.description === 'Staff') || staffRoles?.[0];
    if (!defaultRole) {
      toast.error('No staff roles available');
      return;
    }

    const selectedStaff = staffMembers.filter(staff => selectedStaffKeys.has(staff.key));
    const defaultCategory = accountData?.staffMemberCategories?.[0];

    try {
      const promises = selectedStaff.map(staff =>
        postAccountSiteStaffMember({
          id: 0,
          key: '00000000-0000-0000-0000-000000000000',
          staffMemberUserId: staff.id,
          position: '',
          addedAt: new Date().toISOString(),
          account: currentAccount,
          staffRole: {
            id: defaultRole.id,
            description: defaultRole.description,
            definition: defaultRole.definition,
          },
          staffMemberCategory: defaultCategory
            ? {
                id: defaultCategory.id,
                description: defaultCategory.description,
              }
            : null,
          specialties: [],
        }).unwrap(),
      );

      await Promise.all(promises);
      toast.success(
        `Successfully added ${selectedStaffKeys.size} staff member${selectedStaffKeys.size > 1 ? 's' : ''}`,
      );
      setSelectedStaffKeys(new Set());
      setSearchQuery('');
      setOpen(false);
    } catch {
      toast.error('Failed to add staff members');
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedStaffKeys(new Set());
      setSearchQuery('');
    }
  }, [open]);

  return (
    <Dialog size='4xl' onClose={setOpen} open={open}>
      <div className='flex flex-row justify-between items-center'>
        <DialogTitle>Add Staff Users</DialogTitle>
        <CloseButton as={Button} variant='light' isIconOnly>
          <XMarkIcon className='size-5' />
        </CloseButton>
      </div>
      <DialogBody>
        <div className='mb-4 flex items-center gap-3'>
          <Input
            placeholder='Search by name, email, position, or specialty...'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            startContent={<MagnifyingGlassIcon className='size-5 text-default-400' />}
            isClearable
            onClear={() => setSearchQuery('')}
            className='flex-1'
          />
          {filteredStaffMembers.length > 0 && (
            <Button
              variant='flat'
              color={isAllSelected ? 'primary' : 'default'}
              onPress={handleSelectAll}
              size='md'
            >
              {isAllSelected ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </div>

        <div className='flex-col max-h-[60vh] overflow-y-auto'>
          {filteredStaffMembers.length > 0 ? (
            filteredStaffMembers.map(staff => {
              const isSelected = selectedStaffKeys.has(staff.key);
              const allPositions = _(staff.accountSiteStaffMembers)
                .map(x => `${x.accountName} - ${x.position ?? 'N/A'}`)
                .sort()
                .value();
              const displayedPositions = allPositions.slice(0, 4);
              const remainingCount = allPositions.length - displayedPositions.length;

              const specialties = _(staff.accountSiteStaffMembers)
                .flatMap(x => x.specialties)
                .uniq()
                .sort()
                .join(', ');

              return (
                <div
                  key={staff.id}
                  className={`flex items-start gap-3 px-4 py-3 listGroupItem cursor-pointer hover:bg-default-100 transition-colors ${
                    isSelected ? 'bg-primary-50 border-primary-200' : 'bg-default-50'
                  }`}
                  onClick={() => handleSelectStaff(staff.key)}
                >
                  <div className='pt-1'>
                    <Checkbox
                      isSelected={isSelected}
                      onChange={() => handleSelectStaff(staff.key)}
                      aria-label={`Select ${staff.firstName} ${staff.lastName}`}
                    />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-baseline gap-2 mb-1'>
                      <div className='font-semibold text-base'>
                        {staff.firstName} {staff.lastName}
                      </div>
                      {staff.title && <div className='text-sm text-default-500'>{staff.title}</div>}
                    </div>
                    <div className='text-sm text-default-600 mb-2'>
                      <TextLink href={`mailto:${staff.emailAddress}`}>
                        {staff.emailAddress}
                      </TextLink>
                    </div>
                    {allPositions.length > 0 && (
                      <div className='flex flex-wrap gap-1 mb-1.5'>
                        {displayedPositions.map(position => (
                          <Chip
                            key={position}
                            size='sm'
                            variant='flat'
                            color='default'
                            classNames={{
                              base: 'h-5 px-1.5',
                              content: 'text-[10px] px-1',
                            }}
                          >
                            {position}
                          </Chip>
                        ))}
                        {remainingCount > 0 && (
                          <span className='text-[10px] text-default-500 self-center ml-1'>
                            + {remainingCount} more...
                          </span>
                        )}
                      </div>
                    )}
                    {specialties && (
                      <div className='text-sm text-default-500'>
                        <span className='font-medium'>Specialties:</span> {specialties}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <NoData
              message={
                searchQuery
                  ? 'No one matches that search. Clear the search box or try a different name or email.'
                  : 'No one from this organization is available to add. If they already work here, create them as a new staff member. If they belong to a different organization, ask a MeetingMD admin to free their email on that profile first.'
              }
            />
          )}
        </div>

        <div className='text-center text-sm text-default-500 mt-4'>
          Don't see them? <TextLink href='/staff/new'>Create a new staff member</TextLink>
        </div>
      </DialogBody>
      <DialogActions>
        <Button color='default' variant='flat' onPress={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          color='primary'
          variant='solid'
          onPress={handleAddSelectedUsers}
          isDisabled={selectedStaffKeys.size === 0}
        >
          Add Selected Users ({selectedStaffKeys.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationStaffMemberDialog;
